/* eslint-disable quotes */
import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import {
  Button,
  Pane,
  Accordion,
  AccordionSet,
  Checkbox,
  Loading,
  Row,
  Col,
  InfoPopover,
  KeyValue,
  Icon,
} from "@folio/stripes/components";
import { useOkapiKy, useCallout, useStripes } from "@folio/stripes/core";

import "./SearchFeaturesSettings.css";

const SearchFeaturesSettings = () => {
  const intl = useIntl();
  const ky = useOkapiKy();
  const callout = useCallout();
  const stripes = useStripes();

  // Check permissions for search features
  const hasSearchFeaturesPermission = stripes?.hasPerm?.('search.config.features.collection.get') || false;
  const hasSearchFeaturesWritePermission = stripes?.hasPerm?.('search.config.features.item.post') || false;


  const [features, setFeatures] = useState({
    "search.all.fields": false,
    "browse.classifications": false,
    "browse.cn.intermediate.values": false,
    "browse.cn.intermediate.remove.duplicates": false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingFeatures, setExistingFeatures] = useState({});

  // Feature definitions with descriptions
  const featureDefinitions = [
    {
      id: "search.all.fields",
      category: "search",
      icon: "search",
      tooltipId: "searchFeaturesSettings.feature.searchAllFields.tooltip",
    },
    {
      id: "browse.classifications",
      category: "browse",
      icon: "bookmark",
      tooltipId: "searchFeaturesSettings.feature.browseClassifications.tooltip",
    },
    {
      id: "browse.cn.intermediate.values",
      category: "browse",
      icon: "list",
      tooltipId: "searchFeaturesSettings.feature.browseIntermediateValues.tooltip",
    },
    {
      id: "browse.cn.intermediate.remove.duplicates",
      category: "browse",
      icon: "duplicate",
      tooltipId: "searchFeaturesSettings.feature.removeDuplicates.tooltip",
    },
  ];

  // Fetch current feature configurations
  useEffect(() => {
    if (hasSearchFeaturesPermission) {
      fetchFeatureConfigs();
    } else {
      setLoading(false);
    }
  }, [hasSearchFeaturesPermission]);

  const fetchFeatureConfigs = async () => {
    try {
      setLoading(true);
      const response = await ky.get("search/config/features").json();

      const existingFeaturesMap = {};
      const featuresState = { ...features };

      if (response.features) {
        response.features.forEach((feature) => {
          existingFeaturesMap[feature.feature] = feature;
          featuresState[feature.feature] = feature.enabled;
        });
      }

      setFeatures(featuresState);
      setExistingFeatures(existingFeaturesMap);
    } catch (error) {
      if (callout) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({
            id: "ui-plugin-inventory-templates.searchFeaturesSettings.fetchError",
          }),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveFeatureConfig = async (featureId, enabled) => {
    if (!hasSearchFeaturesPermission) {
      return;
    }

    try {
      setSaving(true);

      const existingFeature = existingFeatures[featureId];

      if (enabled && !existingFeature) {
        // Create new feature configuration
        await ky.post("search/config/features", {
          json: {
            feature: featureId,
            enabled: true,
          },
        });
      } else if (existingFeature) {
        if (enabled) {
          // Update existing configuration
          await ky.put(`search/config/features/${featureId}`, {
            json: {
              feature: featureId,
              enabled: true,
            },
          });
        } else {
          // Delete configuration (disable feature)
          await ky.delete(`search/config/features/${featureId}`);
        }
      }

      // Refresh configurations
      await fetchFeatureConfigs();

      if (callout) {
        callout.sendCallout({
          type: "success",
          message: intl.formatMessage({
            id: enabled
              ? "ui-plugin-inventory-templates.searchFeaturesSettings.featureEnabled"
              : "ui-plugin-inventory-templates.searchFeaturesSettings.featureDisabled",
          }, { feature: intl.formatMessage({ id: `ui-inventory.searchFeaturesSettings.feature.${featureId.replace(/\./g, "_")}` }) }),
        });
      }
    } catch (error) {
      if (callout) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({
            id: "ui-plugin-inventory-templates.searchFeaturesSettings.saveError",
          }),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureToggle = (featureId, enabled) => {
    setFeatures((prev) => ({
      ...prev,
      [featureId]: enabled,
    }));
  };

  const saveAllChanges = async () => {
    if (!hasSearchFeaturesPermission) {
      return;
    }

    try {
      setSaving(true);

      const promises = Object.entries(features).map(([featureId, enabled]) => {
        const existingFeature = existingFeatures[featureId];
        const isCurrentlyEnabled = existingFeature?.enabled || false;

        // Only save if there's a change
        if (enabled !== isCurrentlyEnabled) {
          return saveFeatureConfig(featureId, enabled);
        }
        return Promise.resolve();
      });

      await Promise.all(promises);

      if (callout) {
        callout.sendCallout({
          type: "success",
          message: intl.formatMessage({
            id: "ui-plugin-inventory-templates.searchFeaturesSettings.allSaved",
          }),
        });
      }
    } catch (error) {
      if (callout) {
        callout.sendCallout({
          type: "error",
          message: intl.formatMessage({
            id: "ui-plugin-inventory-templates.searchFeaturesSettings.saveError",
          }),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const renderFeaturesByCategory = (category) => {
    return featureDefinitions
      .filter((def) => def.category === category)
      .map((featureDef) => (
        <Row key={featureDef.id}>
          <Col xs={12}>
            <div className="search-feature-item">
              <Checkbox
                label={
                  <div className="search-feature-label">
                    <Icon icon={featureDef.icon} />
                    <FormattedMessage
                      id={`ui-inventory.searchFeaturesSettings.feature.${featureDef.id.replace(/\./g, "_")}`}
                    />
                  </div>
                }
                checked={features[featureDef.id]}
                onChange={(e) => handleFeatureToggle(featureDef.id, e.target.checked)}
                disabled={saving || !hasSearchFeaturesWritePermission}
              />
              <InfoPopover
                content={
                  <FormattedMessage id={`ui-inventory.${featureDef.tooltipId}`} />
                }
              />
            </div>
            <div className="search-feature-status">
              {existingFeatures[featureDef.id] ? (
                <span className="feature-status-active">
                  <Icon icon="check-circle" />
                  <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.status.active" />
                </span>
              ) : (
                <span className="feature-status-inactive">
                  <Icon icon="times-circle" />
                  <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.status.inactive" />
                </span>
              )}
            </div>
          </Col>
        </Row>
      ));
  };

  if (loading) {
    return <Loading />;
  }

  // Show permission error if user doesn't have access
  if (!hasSearchFeaturesPermission) {
    return (
      <Pane
        defaultWidth="fill"
        fluidContentWidth
        paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.title" />}
      >
        <div className="search-features-settings">
          <div className="search-features-card">
            <div className="permission-error">
              <Icon icon="exclamation-triangle" size="large" />
              <h3>
                <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.permissionError" />
              </h3>
              <p>
                <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.permissionDetails" />
              </p>
            </div>
          </div>
        </div>
      </Pane>
    );
  }

  return (
    <Pane
      defaultWidth="fill"
      fluidContentWidth
      paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.title" />}
    >
      <div className="search-features-settings">
        <div className="search-features-card">
          <div className="search-features-header">
            <Icon icon="cog" size="medium" />
            <h3>
              <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.configuration" />
            </h3>
          </div>

          <AccordionSet>
            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.searchFeatures" />
              }
              id="search-features"
            >
              {renderFeaturesByCategory("search")}
            </Accordion>

            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.browseFeatures" />
              }
              id="browse-features"
            >
              {renderFeaturesByCategory("browse")}
            </Accordion>

            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.performanceImpact" />
              }
              id="performance-impact"
            >
              <Row>
                <Col xs={12}>
                  <div className="performance-info">
                    <Icon icon="exclamation-triangle" />
                    <div>
                      <h4>
                        <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.performanceWarning" />
                      </h4>
                      <ul>
                        <li>
                          <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.performance.searchAllFields" />
                        </li>
                        <li>
                          <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.performance.browseFeatures" />
                        </li>
                        <li>
                          <FormattedMessage id="ui-plugin-inventory-templates.searchFeaturesSettings.performance.indexSize" />
                        </li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </Accordion>
          </AccordionSet>
        </div>

        <div className="search-features-actions">
          <Button
            buttonStyle="primary"
            onClick={saveAllChanges}
            disabled={saving || !hasSearchFeaturesWritePermission}
            loading={saving}
          >
            <FormattedMessage id="ui-plugin-inventory-templates.save" />
          </Button>
        </div>
      </div>
    </Pane>
  );
};

SearchFeaturesSettings.propTypes = {};

export default SearchFeaturesSettings;
